import * as React from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import { getMainLegs } from '@src/shared/src/util/trips';
import { formatDate } from '@src/shared/src/util/date';
import { getVehicleIcon } from '@toolkit/util/app';
import { findBySelected } from '@src/shared/src/util/general';
import { getTariffFareName } from '@src/shared/src/util/tariffs';
// Constants
import { DATE_FORMAT_TYPES, VEHICLE_TYPES } from '@src/shared/src/const/app';

// Actions
// Models
import {
  LegModel,
  TripModel,
  TransportSegmentModel,
  BasketItemModel,
} from '@src/shared/src/models';
// Interfaces
// Components
import { BookingInfoItem } from '@src/components/BookingInfoItem';
// Styles
import '../styles/ConfirmedBookingTransportSegment.scss';

type Props = {
  trip: TripModel;
  basketItems: BasketItemModel[];
};

const ConfirmedBookingTransportSegment: React.FC<Props> = (props) => {
  const { trip, basketItems } = props;
  const { t } = useTranslation();

  const tripLegs = getMainLegs(trip.legs);

  const getFareName = (leg: LegModel) => {
    const tariff = findBySelected(leg.legOption.tariffs);
    return getTariffFareName(basketItems, tariff.id);
  };

  const tripInfo = (leg: LegModel, transportSegment: TransportSegmentModel) => {
    const product =
      transportSegment.vehicles[0] === VEHICLE_TYPES.PLANE
        ? `${transportSegment.operators[0]} ${transportSegment.products[0]}`
        : transportSegment.products.join(', ');

    const fareType = transportSegment.fareName ?? getFareName(leg);
    return `${product} | ${fareType}`;
  };

  return (
    <div className="confirmed-booking-transport-segments">
      {tripLegs.map((leg: LegModel) => {
        return (
          <div key={leg.id}>
            {leg.legOption.transportSegments.map(
              (transportSegment: TransportSegmentModel, index: number) => (
                <div key={transportSegment.id}>
                  <BookingInfoItem
                    icon={
                      index === 0 ? (
                        <i
                          className={`icon ${getVehicleIcon(
                            transportSegment.vehicles[0] as VEHICLE_TYPES,
                          )}`}
                        />
                      ) : null
                    }
                    depName={transportSegment.depName}
                    depAt={
                      transportSegment.depPlatform &&
                      `${t('transportSegment.label.platform.dep')} ${transportSegment.depPlatform}`
                    }
                    depTime={formatDate(transportSegment.depAt, DATE_FORMAT_TYPES.LONG_TIME)}
                    arrName={transportSegment.arrName}
                    arrAt={
                      transportSegment.arrPlatform &&
                      `${t('transportSegment.label.platform.arr')} ${transportSegment.arrPlatform}`
                    }
                    arrTime={formatDate(transportSegment.arrAt, DATE_FORMAT_TYPES.LONG_TIME)}
                    changeover={index != leg.legOption.transportSegments.length - 1}
                    technicalStops={transportSegment.technicalStops}
                  />
                  <div className="confirmed-booking-transport-segments-products">
                    <i
                      className={`icon ${getVehicleIcon(
                        transportSegment.vehicles[0] as VEHICLE_TYPES,
                      )}`}
                    />
                    {tripInfo(leg, transportSegment)}
                  </div>
                </div>
              ),
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConfirmedBookingTransportSegment;
